import { useEffect, useMemo, useState } from 'react';
import { Trophy, BookOpen } from 'lucide-react';
import { db } from '@/lib/db';
import { getSupabase } from '@/lib/db/client';
import type { Course, Game, Member, MemberPublic, MemberWithStats, PointsEntry, PostEnriched } from '@/lib/db/types';
import { clubIndex, type ClubIndexRound } from '@/lib/board/club-index';
import { mergeMemberStats, statsFromPosts } from '@/lib/board/post-stats';
import { buildPersonalCareerStats } from '@/lib/board/personal-records';
import { holesLabel } from '@/lib/scoring/played-par';
import { CAMPUS_ORDER, getCourseCampus, getCourseComboLabel, groupCoursesByCampus } from '@/lib/courses/group';
import { getSeasonTier } from '@/lib/reference/season-tiers';
import { Avatar } from '@/components/shared/Avatar';
import { Crest } from '@/components/shared/Crest';
import { ActionView } from '@/components/board/ActionView';
import { ClubPointsGuideSheet } from '@/components/board/ClubPointsGuideSheet';

type BoardKey = 'bagtag' | 'low' | 'avg' | 'handicap' | 'clubidx' | 'rounds' | 'skins';
type DateRange = 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'this-year' | 'last-year' | 'all-time';
type ViewMode = 'standings' | 'records' | 'action';

interface BoardRow {
  member: MemberWithStats;
  points: number;
  tier: ReturnType<typeof getSeasonTier>;
}

interface Props {
  member: Member;
  onOpenProfile: (id: string) => void;
}

const BOARD_LABELS: Record<BoardKey, string> = {
  bagtag: 'BAG TAG',
  low: 'LOW GROSS',
  avg: 'AVG GROSS',
  handicap: 'INDEX®',
  clubidx: 'ANCC INDEX',
  rounds: 'ROUNDS',
  skins: 'SKINS',
};

const BOARD_DESC: Partial<Record<BoardKey, { title: string; text: string }>> = {
  bagtag: { title: 'Bag Tag · Club Points', text: 'Your in-club rank. Earn points by playing, posting rounds, community activity, and achievements. Status only — no cash value.' },
  low: { title: 'Low Gross', text: "Each member's lowest single-round gross score, ranked low to high." },
  avg: { title: 'Average Gross', text: "Each member's average gross score across all posted rounds, ranked low to high." },
  handicap: { title: 'Handicap Index', text: "Each member's current handicap index, ranked low to high. Self-reported by members." },
  clubidx: { title: 'ANCC Index', text: 'Internal handicap index from rounds posted at ANCC courses. Members need 3+ qualifying rounds to appear.' },
  rounds: { title: 'Rounds Played', text: 'Total rounds each member has posted to Bag Tag.' },
  skins: { title: 'Skins Won', text: 'Total dollar value of skins won across all posted rounds.' },
};

function getDateBounds(range: DateRange): [Date, Date] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (range) {
    case 'this-week': {
      const start = new Date(today);
      start.setDate(start.getDate() - start.getDay());
      return [start, now];
    }
    case 'last-week': {
      const start = new Date(today);
      start.setDate(start.getDate() - start.getDay() - 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return [start, end];
    }
    case 'this-month':
      return [new Date(now.getFullYear(), now.getMonth(), 1), now];
    case 'last-month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return [start, end];
    }
    case 'this-year':
      return [new Date(now.getFullYear(), 0, 1), now];
    case 'last-year':
      return [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)];
    default:
      return [new Date(0), now];
  }
}

function pointsInRange(entries: PointsEntry[], memberId: string, range: DateRange): number {
  if (range === 'all-time') return entries.filter((e) => e.member_id === memberId).reduce((s, e) => s + e.points, 0);
  const [start, end] = getDateBounds(range);
  const startMs = start.getTime();
  const endMs = end.getTime();
  return entries
    .filter((e) => e.member_id === memberId && new Date(e.created_at).getTime() >= startMs && new Date(e.created_at).getTime() <= endMs)
    .reduce((s, e) => s + e.points, 0);
}

export function BoardTab({ member, onOpenProfile }: Props) {
  const [view, setView] = useState<ViewMode>('standings');
  const [board, setBoard] = useState<BoardKey>('bagtag');
  const [dateRange, setDateRange] = useState<DateRange>('all-time');
  const [recordsTab, setRecordsTab] = useState<'course' | 'personal' | 'recent'>('course');

  const chipBtn = (active: boolean) =>
    `shrink-0 rounded-full px-3.5 py-2 text-[13px] font-bold ${
      active
        ? 'border border-navy bg-navy text-cream shadow-bt'
        : 'bt-flat border border-line bg-paper text-ink-soft'
    }`;
  const [rows, setRows] = useState<BoardRow[]>([]);
  const [ledger, setLedger] = useState<PointsEntry[]>([]);
  const [posts, setPosts] = useState<PostEnriched[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [courses, setCourses] = useState<Record<string, string>>({});
  const [courseList, setCourseList] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allMembers, setAllMembers] = useState<MemberPublic[]>([]);
  const [periodPointsMap, setPeriodPointsMap] = useState<Record<string, number>>({});
  const [standingsExpanded, setStandingsExpanded] = useState(false);
  const [showPointsGuide, setShowPointsGuide] = useState(false);

  useEffect(() => {
    if (dateRange === 'all-time') {
      setPeriodPointsMap({});
      return;
    }
    const [start, end] = getDateBounds(dateRange);
    void db.points.clubTotalsInRange(start.toISOString(), new Date(end.getTime() + 86400000).toISOString())
      .then((rows) => {
        setPeriodPointsMap(Object.fromEntries(rows.map((r) => [r.member_id, r.total_points])));
      })
      .catch(() => setPeriodPointsMap({}));
  }, [dateRange]);

  const loadBoard = async () => {
      try {
        setLoading(true);
      const [stats, postsData, coursesData, ledgerData, gamesData] = await Promise.all([
        db.members.listWithStats({ limit: 100 }),
        db.posts.list({ limit: 200 }),
        db.courses.list(),
        getSupabase().from('points_ledger').select('*').order('created_at', { ascending: false }).limit(2000),
        db.games.list(),
      ]);
      const entries = (ledgerData.data || []) as PointsEntry[];
      const postStats = statsFromPosts(postsData);
      setAllMembers(stats.map((s) => s));
      setRows(stats.map((m) => {
        const merged = mergeMemberStats(m, postStats[m.id]);
        return {
          member: { ...m, ...merged },
          points: m.total_points,
          tier: getSeasonTier(m.total_points),
        };
      }));
      setLedger(entries);
      setPosts(postsData);
      setGames(gamesData);
      setCourseList(coursesData);
      setCourses(Object.fromEntries(coursesData.map((c) => [c.id, c.name])));
        setError(null);
      } catch (e) {
        setError((e as Error).message || 'Unable to load board');
      } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadBoard();
  }, []);

  useEffect(() => {
    const onRefresh = () => { void loadBoard(); };
    const onFocus = () => { void loadBoard(); };
    window.addEventListener('bagtag:refresh-feed', onRefresh);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('bagtag:refresh-feed', onRefresh);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const clubIndexRounds = useMemo((): ClubIndexRound[] => {
    return posts
      .filter((p) => p.type === 'round' && p.round?.gross_total != null && p.round.holes === 'full' && p.round.tee)
      .map((p) => ({
        member_id: p.author_id,
        gross_total: p.round!.gross_total!,
        holes: p.round!.holes,
        course_rating: p.round!.tee!.course_rating,
        slope: p.round!.tee!.slope,
        played_at: p.round!.played_at,
      }));
  }, [posts]);

  const lowGrossCourseByMember = useMemo(() => {
    const best: Record<string, { gross: number; course: string }> = {};
    posts.forEach((p) => {
      if (p.type !== 'round' || p.round?.gross_total == null || p.round.holes !== 'full') return;
      const gross = p.round.gross_total;
      const course = (p.round.course?.name || courses[p.round.course_id] || '')
        .replace(/^ANCC /, '')
        .replace(' — ', ' ');
      const prev = best[p.author_id];
      if (!prev || gross < prev.gross) best[p.author_id] = { gross, course };
    });
    return Object.fromEntries(Object.entries(best).map(([id, v]) => [id, v.course]));
  }, [posts, courses]);

  const skinsByMember = useMemo(() => {
    const totals: Record<string, number> = {};
    games.forEach((g) => {
      if (!g.format_id.includes('skin') && g.format_id !== 'skins') return;
      const settlement = g.config?.settlement as { net?: Record<string, number> } | undefined;
      if (!settlement?.net) return;
      Object.entries(settlement.net).forEach(([key, val]) => {
        if (val > 0) totals[key] = (totals[key] || 0) + val;
      });
    });
    return totals;
  }, [games]);

  const sorted = useMemo(() => {
    const withPeriod = rows.map((r) => {
      const periodPoints = dateRange === 'all-time' ? r.points : (periodPointsMap[r.member.id] ?? 0);
      const ptsForTier = dateRange === 'all-time' ? r.points : periodPoints;
      return {
        ...r,
        periodPoints,
        tier: getSeasonTier(ptsForTier),
        anccIndex: clubIndex(clubIndexRounds, r.member.id),
        skinsWon: skinsByMember[r.member.id] ?? 0,
      };
    });
    const sorters: Record<BoardKey, (a: typeof withPeriod[0], b: typeof withPeriod[0]) => number> = {
      bagtag: (a, b) => (dateRange === 'all-time' ? b.points : b.periodPoints) - (dateRange === 'all-time' ? a.points : a.periodPoints),
      low: (a, b) => (a.member.low_gross ?? 999) - (b.member.low_gross ?? 999),
      avg: (a, b) => (a.member.avg_gross ?? 999) - (b.member.avg_gross ?? 999),
      handicap: (a, b) => (a.member.handicap ?? 99) - (b.member.handicap ?? 99),
      clubidx: (a, b) => (a.anccIndex ?? 99) - (b.anccIndex ?? 99),
      rounds: (a, b) => b.member.rounds_count - a.member.rounds_count,
      skins: (a, b) => b.skinsWon - a.skinsWon,
    };
    return [...withPeriod].sort(sorters[board]);
  }, [rows, board, dateRange, periodPointsMap, clubIndexRounds, skinsByMember]);

  const valueOf = (r: BoardRow & { periodPoints: number; anccIndex?: number | null; skinsWon?: number }) => {
    if (board === 'bagtag') {
      const pts = dateRange === 'all-time' ? r.points : r.periodPoints;
      return pts > 0 ? pts : '—';
    }
    if (board === 'low') return r.member.low_gross ?? '—';
    if (board === 'avg') return r.member.avg_gross != null ? r.member.avg_gross.toFixed(1) : '—';
    if (board === 'handicap') return r.member.handicap ?? '—';
    if (board === 'clubidx') return r.anccIndex != null ? r.anccIndex.toFixed(1) : '—';
    if (board === 'rounds') return r.member.rounds_count;
    return `$${r.skinsWon ?? 0}`;
  };

  const subline = (r: BoardRow) => {
    if (board === 'bagtag') return `${r.tier.badge} ${r.tier.name} · ${r.member.rounds_count} round${r.member.rounds_count === 1 ? '' : 's'}`;
    if (board === 'low') {
      const course = lowGrossCourseByMember[r.member.id];
      return course || (r.member.low_gross != null ? 'Club low gross' : 'No rounds');
    }
    if (board === 'avg') return `${r.member.rounds_count} rounds · HCP ${r.member.handicap ?? '—'}`;
    if (board === 'handicap') return r.member.handicap_updated_at
      ? `Updated ${new Date(r.member.handicap_updated_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
      : 'Self-reported';
    if (board === 'clubidx') {
      const idx = (r as BoardRow & { anccIndex?: number | null }).anccIndex;
      if (idx == null) return `${r.member.rounds_count > 0 ? `${r.member.rounds_count} rounds · need 3+ to qualify` : 'No qualifying rounds'}`;
      return `From posted rounds · GHIN ${r.member.handicap ?? '—'}`;
    }
    if (board === 'skins') return 'Skins game winnings';
    if (board === 'rounds') return `Avg ${r.member.avg_gross?.toFixed(1) ?? '—'} · Hcp ${r.member.handicap ?? '—'}`;
    return `${r.member.rounds_count} rounds`;
  };

  const meRank = sorted.findIndex((r) => r.member.id === member.id) + 1;
  const myRow = meRank > 0 ? sorted[meRank - 1] : null;
  const bagtagDateOptions: Array<[DateRange, string]> = [
    ['this-week', 'Week'],
    ['this-month', 'Month'],
    ['this-year', 'Year'],
    ['all-time', 'All-time'],
  ];
  const bagtagPointsOf = (r: (typeof sorted)[number]) => (dateRange === 'all-time' ? r.points : r.periodPoints);
  const myBagtagPoints = myRow ? bagtagPointsOf(myRow) : 0;
  const leadDelta = (() => {
    if (!myRow || meRank <= 0 || sorted.length <= 1) return null;
    if (meRank === 1) return { kind: 'lead' as const, points: myBagtagPoints - bagtagPointsOf(sorted[1]) };
    return { kind: 'behind' as const, points: bagtagPointsOf(sorted[meRank - 2]) - myBagtagPoints };
  })();
  const myRoundCount = myRow?.member.rounds_count ?? 0;
  const hotStreak = Math.min(7, Math.max(0, myRoundCount));

  const courseRecords = useMemo(() => {
    const recs: Record<string, { best: number | null; holder: MemberPublic | null; count: number }> = {};
    Object.entries(courses).forEach(([id]) => { recs[id] = { best: null, holder: null, count: 0 }; });
    posts.forEach((p) => {
      if (p.type !== 'round' || !p.round?.gross_total || !p.round.course_id) return;
      if (p.round.holes !== 'full') return;
      const rec = recs[p.round.course_id];
      if (!rec) return;
      rec.count += 1;
      if (rec.best === null || p.round.gross_total < rec.best) {
        rec.best = p.round.gross_total;
        rec.holder = p.author;
      }
    });
    return recs;
  }, [posts, courses]);

  const myRoundPosts = useMemo(() => posts.filter((p) => p.author_id === member.id && p.type === 'round' && p.round?.gross_total), [posts, member.id]);

  const personalCareer = useMemo(
    () => buildPersonalCareerStats(myRoundPosts, courseList),
    [myRoundPosts, courseList],
  );

  function scoreTone(score: number, par: number): string {
    const d = score - par;
    if (d <= -2) return 'text-green-600';
    if (d <= 0) return 'text-navy';
    if (d <= 2) return 'text-ink-soft';
    return 'text-red-600';
  }

  function scoreLabel(score: number, par: number): string {
    const d = score - par;
    if (d === 0) return 'E';
    return d > 0 ? `+${d}` : `${d}`;
  }

  if (loading) return <div className="p-4 pt-4 text-sm text-mute">Loading board…</div>;
  if (error) return <div className="p-4 pt-4 rounded-2xl bg-red-50 text-sm text-red-700">{error}</div>;

  return (
    <div className="space-y-4 pt-4">
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {(['standings', 'records', 'action'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={chipBtn(view === v)}
          >
            {v === 'standings' ? 'Standings' : v === 'records' ? 'Records' : 'Action'}
          </button>
        ))}
      </div>

      {view === 'standings' && (
        <>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {(Object.keys(BOARD_LABELS) as BoardKey[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setBoard(k)}
                className={chipBtn(board === k)}
              >
                {BOARD_LABELS[k]}
              </button>
            ))}
          </div>

          {board === 'bagtag' ? (
            <>
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {bagtagDateOptions.map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setDateRange(id)}
                    className={chipBtn(dateRange === id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="rounded-[24px] border border-gold/30 bg-gradient-to-br from-[#0d264f] to-[#0a1e3f] p-5 text-cream shadow-bt-md">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">Your standing</div>
                <div className="mt-2 inline-flex items-center rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-bold text-gold">
                  {myRow?.tier.badge ?? '◆'} {myRow?.tier.name ?? 'Member'}
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="font-serif text-6xl font-bold leading-none text-gold">
                      {myRow ? bagtagPointsOf(myRow) : 0}
                    </div>
                    <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">Club points</div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-4xl font-bold">#{meRank || '—'}</div>
                    <div className="text-[11px] text-white/60">of {sorted.length}</div>
          </div>
        </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className="rounded-xl border border-white/10 bg-white/5 p-2 text-center">
                    <div className="font-serif text-2xl font-bold">{myRoundCount}</div>
                    <div className="text-[10px] uppercase tracking-wide text-white/60">Rounds</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-2 text-center">
                    <div className="font-serif text-2xl font-bold text-gold">{hotStreak}</div>
                    <div className="text-[10px] uppercase tracking-wide text-white/60">Streak</div>
        </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-2 text-center">
                    <div className="font-serif text-2xl font-bold text-green-300">
                      {leadDelta ? `${leadDelta.kind === 'lead' ? '+' : '-'}${leadDelta.points}` : '—'}
      </div>
                    <div className="text-[10px] uppercase tracking-wide text-white/60">
                      {leadDelta?.kind === 'lead' ? 'Lead' : 'Behind'}
                    </div>
                  </div>
                </div>
                <div className="mt-3 rounded-xl border border-green-400/30 bg-green-400/10 p-3 text-sm">
                  {leadDelta
                    ? leadDelta.kind === 'lead'
                      ? `You are leading by ${leadDelta.points} points in this ${dateRange === 'all-time' ? 'all-time' : 'period'} view.`
                      : `You are ${leadDelta.points} points from moving up one place.`
                    : 'Play and post to climb the standings.'}
                </div>
                <button
                  type="button"
                  onClick={() => setShowPointsGuide(true)}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gold/50 bg-gold/10 py-2 text-xs font-bold text-gold-bright"
                >
                  <BookOpen size={14} />
                  How to earn points
                </button>
              </div>

              <div className="mt-2 flex items-center justify-between">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-deep">The Podium</div>
                <div className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gold-deep">
                  Bag Tag points
                </div>
              </div>
              <div className="grid grid-cols-3 items-end gap-2">
                {[1, 0, 2].map((idx) => {
                  const r = sorted[idx];
                  if (!r) return <div key={`empty-${idx}`} />;
                  const rank = idx + 1;
                  const isFirst = idx === 0;
                  return (
                    <button
                      key={r.member.id}
                      type="button"
                      onClick={() => onOpenProfile(r.member.id)}
                      className={`rounded-2xl p-3 text-center shadow-bt ${isFirst ? 'border border-gold/40 bg-navy text-cream' : 'border border-line bg-paper text-ink'}`}
                    >
                      <div className={`mx-auto mb-2 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${isFirst ? 'bg-gold text-navy' : 'bg-cream text-ink'}`}>
                        {rank}
                      </div>
                      <div className="mb-2 flex justify-center">
                        <Avatar member={r.member} size={isFirst ? 52 : 42} />
                      </div>
                      <div className={`truncate text-xs font-bold ${isFirst ? 'text-cream' : 'text-ink'}`}>{r.member.name}</div>
                      <div className={`font-serif text-2xl font-bold ${isFirst ? 'text-gold' : 'text-navy'}`}>{bagtagPointsOf(r)}</div>
                      <div className={`text-[9px] uppercase tracking-wide ${isFirst ? 'text-white/60' : 'text-mute'}`}>Points</div>
                    </button>
                  );
                })}
              </div>

              <div className={`rounded-2xl border border-line bg-paper ${standingsExpanded ? 'pb-3' : ''}`}>
                <button
                  type="button"
                  onClick={() => setStandingsExpanded((v) => !v)}
                  className="flex w-full items-center justify-between px-4 py-4 text-left"
                >
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-deep">See full standings</div>
                    <div className="text-xs text-mute">All {sorted.length} players</div>
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-cream">
                    {standingsExpanded ? '▲' : '▼'}
                  </div>
                </button>
                {standingsExpanded && (
                  <div className="space-y-1 px-2">
                    {sorted.map((r, i) => {
                      const isMe = r.member.id === member.id;
                      const points = bagtagPointsOf(r);
                      return (
                        <button
                          key={r.member.id}
                          type="button"
                          onClick={() => onOpenProfile(r.member.id)}
                          className={`grid w-full grid-cols-[28px_40px_1fr_auto] items-center gap-3 rounded-xl px-2 py-2 text-left ${isMe ? 'border border-gold/50 bg-gold/10' : ''}`}
                        >
                          <div className={`text-center font-serif text-lg ${isMe ? 'text-gold' : 'text-mute'}`}>{i + 1}</div>
                          <Avatar member={r.member} size={36} />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-ink">
                              {r.member.name}{isMe && <span className="ml-1 rounded bg-gold px-1 py-0.5 text-[9px] font-bold text-navy">YOU</span>}
                            </div>
                            <div className="text-[10px] uppercase tracking-wide text-mute">{r.tier.name} · {r.member.rounds_count} rounds</div>
                          </div>
                          <div className="text-right">
                            <div className="font-serif text-lg font-bold text-navy">{points}</div>
                            <div className="text-[10px] text-mute">{dateRange === 'all-time' ? 'career' : 'period'}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {BOARD_DESC[board] && (
                <div className="rounded-xl border border-line bg-paper p-3 text-[11px] leading-relaxed text-ink-soft shadow-bt">
                  <strong className="text-navy">{BOARD_DESC[board]!.title}</strong> — {BOARD_DESC[board]!.text}
                </div>
              )}

              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {([
                  ['this-week', 'This Week'],
                  ['last-week', 'Last Week'],
                  ['this-month', 'This Month'],
                  ['last-month', 'Last Month'],
                  ['this-year', 'This Year'],
                  ['last-year', 'Last Year'],
                  ['all-time', 'All Time'],
                ] as const).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setDateRange(id)}
                    className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${dateRange === id ? 'bg-gold text-navy shadow-bt' : 'border border-line bg-paper text-ink-soft'}`}
                  >
                    {label}
                  </button>
                ))}
                <span className="shrink-0 rounded-full border border-line bg-cream px-3 py-1.5 text-xs font-semibold text-mute opacity-60">
                  Custom · Soon
                </span>
              </div>

              {myRow && (
                <div className="relative overflow-hidden rounded-[18px] bg-navy p-[18px] text-cream shadow-bt-md">
                  <div className="pointer-events-none absolute -right-5 -top-5 opacity-[0.08]">
                    <Crest size={120} variant="full" />
                  </div>
                  <div className="flex items-center gap-3.5">
                    <div className="font-serif text-[40px] font-bold leading-none text-gold">#{meRank}</div>
                    <div className="flex-1">
                      <div className="text-xs text-white/55">Your rank · {BOARD_LABELS[board]}</div>
                      <div className="font-serif text-3xl font-bold">{valueOf(myRow)}</div>
                      <div className="text-xs text-white/55">{subline(myRow)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-white/55">of</div>
                      <div className="font-serif text-3xl font-bold">{sorted.length}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-3 space-y-2">
                {sorted.map((r, i) => {
                  const isMe = r.member.id === member.id;
                  return (
                    <button
                      key={r.member.id}
                      type="button"
                      onClick={() => onOpenProfile(r.member.id)}
                      className={`flex w-full items-center gap-3 rounded-[14px] border border-line bg-paper p-3.5 text-left shadow-bt ${isMe ? 'ring-1 ring-gold' : ''}`}
                    >
                      <div className={`min-w-[26px] font-serif text-lg font-bold ${i < 3 ? 'text-gold' : 'text-mute'}`}>{i + 1}</div>
                      <Avatar member={r.member} size={38} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-serif text-[15px] font-semibold text-navy">
                          {r.member.name}{isMe && <span className="text-gold"> · You</span>}
                        </div>
                        <div className="truncate text-xs text-mute">{subline(r)}</div>
                      </div>
                      <div className="font-serif text-lg font-bold text-navy">{valueOf(r)}</div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {view === 'records' && (
        <>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            <button type="button" onClick={() => setRecordsTab('course')} className={chipBtn(recordsTab === 'course')}>Course Records</button>
            <button type="button" onClick={() => setRecordsTab('personal')} className={chipBtn(recordsTab === 'personal')}>My Records</button>
            <button type="button" onClick={() => setRecordsTab('recent')} className={chipBtn(recordsTab === 'recent')}>Recent</button>
          </div>

          {recordsTab === 'course' && (
            <div className="space-y-2">
              <p className="text-xs leading-[1.5] text-ink-soft">Lowest gross scores recorded on each ANCC course by any member.</p>
              {CAMPUS_ORDER.map((campus) => {
                const grouped = groupCoursesByCampus(courseList)[campus];
                if (!grouped.length) return null;
                return (
                  <div key={campus} className="mb-4">
                    <div className="mb-2 ml-1 font-serif text-[13px] font-bold uppercase tracking-wider text-gold-deep">{campus}</div>
                    {grouped.map((course) => {
                      const rec = courseRecords[course.id];
                      const par = course.par;
                      return (
                        <div key={course.id} className={`mb-2 rounded-[14px] bg-paper p-3.5 shadow-bt ${rec?.best != null ? 'border border-line' : 'border border-dashed border-line'}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="font-serif text-[15px] font-bold text-ink">{getCourseComboLabel(course)}</div>
                              <div className="mt-0.5 text-[11px] text-mute">Par {par} · {rec?.count ?? 0} round{(rec?.count ?? 0) === 1 ? '' : 's'} recorded</div>
                            </div>
                            {rec?.best != null ? (
                              <div className="text-right">
                                <div className={`font-serif text-[26px] font-bold leading-none ${scoreTone(rec.best, par)}`}>{rec.best}</div>
                                <div className={`font-mono text-[10px] font-bold ${scoreTone(rec.best, par)}`}>{scoreLabel(rec.best, par)}</div>
                              </div>
                            ) : (
                              <div className="text-xs font-medium text-mute">No record yet</div>
                            )}
                          </div>
                          {rec?.holder && (
                            <button type="button" onClick={() => onOpenProfile(rec.holder!.id)} className="mt-2.5 flex w-full items-center gap-2 border-t border-line pt-2 text-left">
                              <Avatar member={rec.holder} size={24} />
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-bold text-navy">
                                  {rec.holder.name}
                                  {rec.holder.id === member.id && <span className="text-gold-deep"> · You</span>}
                                </div>
                              </div>
                              <Trophy size={16} className="text-gold" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {recordsTab === 'personal' && (
            <div className="space-y-3">
              {!personalCareer ? (
                <div className="rounded-[14px] bg-paper p-6 text-center text-sm text-mute shadow-bt">No rounds yet. Play your first round to start tracking personal records.</div>
              ) : (
                <>
                  <div className="relative overflow-hidden rounded-[18px] bg-navy p-4 text-cream shadow-md">
                    <div className="pointer-events-none absolute -right-5 -top-5 opacity-[0.08]">
                      <Crest size={120} variant="full" />
                    </div>
                    <div className="relative mb-3.5 flex items-baseline gap-2">
                      <Trophy size={20} className="text-gold" />
                      <div className="font-serif text-base font-bold">Your Career</div>
                    </div>
                    <div className="relative grid grid-cols-3 gap-2.5">
                      <div>
                        <div className="text-[10px] font-semibold tracking-wider text-white/55">BEST</div>
                        <div className="mt-1 font-serif text-[28px] font-bold leading-none text-gold">{personalCareer.allTimeBest ?? '—'}</div>
                        <div className="mt-1 text-[9px] text-white/60">18 holes</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-semibold tracking-wider text-white/55">AVG</div>
                        <div className="mt-1 font-serif text-[28px] font-bold leading-none">{personalCareer.allTimeAvg}</div>
                        <div className="mt-1 text-[9px] text-white/60">All time</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-semibold tracking-wider text-white/55">L10 AVG</div>
                        <div className="mt-1 font-serif text-[28px] font-bold leading-none">{personalCareer.last10Avg}</div>
                        <div className="mt-1 text-[9px] text-white/60">Last 10</div>
                      </div>
                    </div>
                    <div className="relative mt-3.5 text-[11px] text-white/50">
                      {personalCareer.totalFullRounds} full round{personalCareer.totalFullRounds === 1 ? '' : 's'}
                      {personalCareer.totalAllRounds > personalCareer.totalFullRounds
                        ? ` · ${personalCareer.totalAllRounds - personalCareer.totalFullRounds} nine-hole also posted`
                        : ''}
                    </div>
                  </div>

                  <div className="ml-1 font-serif text-[13px] font-bold uppercase tracking-wider text-gold-deep">Your Best by Course</div>
                  <p className="text-xs text-mute">Full 18-hole rounds only, sorted low to high.</p>
                  {personalCareer.byCourse.length === 0 ? (
                    <div className="rounded-[14px] bg-paper p-4 text-center text-sm text-mute shadow-bt">No full 18-hole rounds yet.</div>
                  ) : personalCareer.byCourse.map((r) => (
                    <div key={r.course.id} className="flex items-center justify-between rounded-[14px] bg-paper px-4 py-3 shadow-bt">
                      <div>
                        <div className="font-serif text-sm font-bold text-ink">{r.course.name}</div>
                        <div className="text-[10px] text-mute">{getCourseCampus(r.course)} · Par {r.course.par}</div>
                      </div>
                      <div className={`font-serif text-2xl font-bold ${scoreTone(r.best, r.course.par)}`}>{r.best}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {recordsTab === 'recent' && (
            <div className="space-y-3">
              <p className="text-xs leading-[1.5] text-ink-soft">Your recent full 18-hole rounds, newest first.</p>
              {!personalCareer || personalCareer.recentFullRounds.length === 0 ? (
                <div className="rounded-[14px] border border-line bg-paper p-4 text-center text-sm text-mute shadow-bt">No full rounds yet.</div>
              ) : personalCareer.recentFullRounds.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-[14px] border border-line bg-paper p-3 shadow-bt">
                  <div className="text-sm text-navy">
                    {courses[p.round!.course_id] || 'Course'}
                    <span className="ml-1.5 text-[10px] text-mute">{holesLabel(p.round!.holes ?? 'full')}</span>
                  </div>
                  <div className="font-serif text-lg font-bold text-navy">{p.round!.gross_total}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === 'action' && (
        <ActionView member={member} members={allMembers} onOpenProfile={onOpenProfile} />
      )}

      {showPointsGuide && (
        <ClubPointsGuideSheet onClose={() => setShowPointsGuide(false)} />
      )}
    </div>
  );
}
